import React, { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import EditIcon from '@mui/icons-material/Edit';
import ReorderIcon from '@mui/icons-material/Reorder';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ListItemText from '@mui/material/ListItemText';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import {Menu, PopoverOrigin, Typography} from '@mui/material';
import { css } from '@emotion/css';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';

export enum RowMenuOptionEffect {
	Reorder = 'reorder',
	MoveToTop = 'moveToTop'
}

export type RowMenuItem = object & {id: string, isArchived?: boolean};

interface RowMenuOptionProps {keyboardShortcut?: boolean}

export interface RowMenuOption {
	content: React.FC<RowMenuOptionProps>;
	onClick: ((arg: RowMenuItem) => void) | RowMenuOptionEffect;
	linkTo?: string;
	contentProps?: RowMenuOptionProps;
	displayCondition?: (item: RowMenuItem) => boolean,
	key: string,
}

export interface RowMenuProps {
	// @deprecated use children instead
	options: RowMenuOption[];
	anchorEl?: Element | null;
	onMenuBtnClick?: (item: RowMenuItem, el?: Element) => void;
	onRowReorderClick?: (item: RowMenuItem) => void;
	onRowMoveToTopClick?: (item: RowMenuItem) => void;
	onClose: () => void;
	item: RowMenuItem | null;
	children?: React.ReactNode;
	anchorOrigin?: PopoverOrigin;
	transformOrigin?: PopoverOrigin;
}

export const RowMenuOptionArchive = () => <>
	<ListItemIcon>
		<ArchiveIcon fontSize="small" />
	</ListItemIcon>
	<ListItemText>Archive</ListItemText>
</>;

export const RowMenuOptionUnarchive = () => <>
	<ListItemIcon>
		<UnarchiveIcon fontSize="small" />
	</ListItemIcon>
	<ListItemText>Unarchive</ListItemText>
</>;

export const RowMenuOptionDelete = ({keyboardShortcut}: RowMenuOptionProps) => <>
	<ListItemIcon>
		<DeleteForeverIcon fontSize="small" />
	</ListItemIcon>
	<ListItemText>Delete</ListItemText>
	{keyboardShortcut && <Typography variant="body2">
		backspace
	</Typography>}
</>;

export const RowMenuOptionGoTo = ({keyboardShortcut}: RowMenuOptionProps) => <>
	<ListItemIcon>
		<ArrowForwardIcon fontSize="small" />
	</ListItemIcon>
	<ListItemText>View</ListItemText>
	{keyboardShortcut && <Typography variant="body2">
	enter
	</Typography>}
</>;

export const RowMenuOptionEdit = ({keyboardShortcut}: RowMenuOptionProps) => <>
	<ListItemIcon>
		<EditIcon fontSize="small" />
	</ListItemIcon>
	<ListItemText>Edit</ListItemText>
	{keyboardShortcut && <Typography variant="body2">
	spacebar
	</Typography>}
</>;

export const RowMenuOptionReorder = () => <>
	<ListItemIcon>
		<ReorderIcon fontSize="small" />
	</ListItemIcon>
	<ListItemText>Reorder</ListItemText>
</>;

export const RowMenuOptionClip = ({keyboardShortcut}: RowMenuOptionProps) => <>
	<ListItemIcon>
		<ContentCutIcon fontSize="small" />
	</ListItemIcon>
	<ListItemText>Cut</ListItemText>
	{keyboardShortcut && <Typography variant="body2">
		⌘X
	</Typography>}
</>;

export const RowMenuOptionMoveToTop = () => <>
	<ListItemIcon>
		<VerticalAlignTopIcon fontSize="small" />
	</ListItemIcon>
	<ListItemText>Move to top</ListItemText>
</>;

export const EntryMenu = ({item, options, onClose, onRowReorderClick, onRowMoveToTopClick, anchorEl, children, anchorOrigin = {vertical: 'top', horizontal: 'left'}, transformOrigin = {vertical: 'top', horizontal: 'left'}}: RowMenuProps) => {
	// TODO: this caching causes: Warning: Internal React error: Expected static flag was missing. Please notify the React team.
	const [cachedItem, setCachedItem] = useState<RowMenuItem | null>(null);
	useEffect(() => {
		if (item) {
			setCachedItem(item);
		}
	}, [item]);

	return <Menu
		id="demo-positioned-menu"
		aria-labelledby="demo-positioned-button"
		anchorEl={anchorEl}
		open={Boolean(item)}
		onClose={() => onClose()}
		anchorOrigin={anchorOrigin}
		className={css`
				& .MuiPaper-root {
					min-width: 240px;
				}
			`}
		transformOrigin={transformOrigin}
	>
		{children}
		{!children && options
			.filter(({displayCondition}) => (cachedItem && displayCondition) ? displayCondition(cachedItem) : true)
			.map(({ content, onClick, linkTo, contentProps, key }) => {
				const MenuItemContent = content;
				if (linkTo) {
					return <Link to={linkTo} key={key}><MenuItemContent {...(contentProps ? contentProps : {})} /></Link>;
				}
				if (typeof onClick === 'function') {
					return <MenuItem key={key} onClick={() => onClick(cachedItem as RowMenuItem)}><MenuItemContent {...(contentProps ? contentProps : {})} /></MenuItem>;
				}
				if (onClick === RowMenuOptionEffect.Reorder) {
					return <MenuItem key={key} onClick={onRowReorderClick ? () => onRowReorderClick(cachedItem as RowMenuItem) : undefined}><MenuItemContent {...(contentProps ? contentProps : {})} /></MenuItem>;
				}
				if (onClick === RowMenuOptionEffect.MoveToTop) {
					return <MenuItem key={key} onClick={onRowMoveToTopClick ? () => onRowMoveToTopClick(cachedItem as RowMenuItem) : undefined}><MenuItemContent {...(contentProps ? contentProps : {})} /></MenuItem>;
				}
			})}
	</Menu>;
};

export default EntryMenu;